import { api, type PaginatedResponse } from './client';

// =====================================================
// Types
// =====================================================

export interface TemplateListItem {
  id: string;
  nome: string;
  finalidade_id: string | null;
  categoria: string;
  versao_atual: number | null;
  updated_at: string | null;
}

export interface TemplateVersaoResponse {
  id: string;
  versao: number;
  conteudo: string;
  is_current: boolean;
  created_at: string;
  created_by_nome?: string;
}

export interface TemplateDetail {
  id: string;
  nome: string;
  finalidade_id: string | null;
  finalidade_descricao: string | null;
  categoria: string;
  variaveis_disponiveis: string[];
  versao_atual: TemplateVersaoResponse | null;
}

export interface TemplateFinalidadeResponse {
  nome: string;
  descricao: string | null;
  categoria: string;
  variaveis_disponiveis: string[];
  assunto_padrao: string | null;
  metadata: Record<string, unknown> | null;
  ativo: boolean;
  binding_atual: {
    template_id: string;
    template_nome: string;
    versao_id: string;
    versao_numero: number;
  } | null;
}

export interface TemplatePreviewResponse {
  html: string;
  texto: string;
}

export interface EmailQueueItem {
  id: string;
  to_email: string;
  to_name: string | null;
  subject: string;
  status: string;
  attempts: number;
  max_attempts: number;
  sent_at: string | null;
  created_at: string;
  next_attempt_at: string | null;
  last_error: string | null;
  finalidade_id: string | null;
}

export interface EmailQueueDetail extends EmailQueueItem {
  body_html: string;
  body_text: string | null;
  template_vars: Record<string, unknown> | null;
  usuario_id: string | null;
  entity_type: string | null;
  entity_id: string | null;
  provider_id: string | null;
  updated_at: string;
}

export interface EmailQueueStats {
  pending: number;
  processing: number;
  sent: number;
  failed: number;
}

export type EmailQueueListResponse = PaginatedResponse<EmailQueueItem>;

// =====================================================
// Templates
// =====================================================

export async function fetchTemplates(categoria?: string): Promise<TemplateListItem[]> {
  const { data } = await api.get<TemplateListItem[]>('/admin/templates/', { params: { categoria } });
  return data;
}

export async function fetchTemplate(id: string): Promise<TemplateDetail> {
  const { data } = await api.get<TemplateDetail>(`/admin/templates/${id}`);
  return data;
}

export async function createTemplate(body: {
  nome: string;
  finalidade_id: string;
  categoria: string;
  conteudo: string;
}): Promise<TemplateDetail> {
  const { data } = await api.post<TemplateDetail>('/admin/templates/', body);
  return data;
}

// =====================================================
// Versões
// =====================================================

export async function fetchVersions(templateId: string): Promise<TemplateVersaoResponse[]> {
  const { data } = await api.get<TemplateVersaoResponse[]>(`/admin/templates/${templateId}/versoes`);
  return data;
}

export async function fetchVersion(templateId: string, versaoId: string): Promise<TemplateVersaoResponse> {
  const { data } = await api.get<TemplateVersaoResponse>(`/admin/templates/${templateId}/versoes/${versaoId}`);
  return data;
}

export async function createVersion(templateId: string, conteudo: string): Promise<TemplateVersaoResponse> {
  const { data } = await api.post<TemplateVersaoResponse>(`/admin/templates/${templateId}/versoes`, { conteudo });
  return data;
}

export async function restoreVersion(templateId: string, versaoId: string): Promise<TemplateVersaoResponse> {
  const { data } = await api.post<TemplateVersaoResponse>(`/admin/templates/${templateId}/versoes/${versaoId}/restaurar`);
  return data;
}

// =====================================================
// Finalidades
// =====================================================

export async function fetchFinalidades(categoria?: string): Promise<TemplateFinalidadeResponse[]> {
  const { data } = await api.get<TemplateFinalidadeResponse[]>('/admin/templates/finalidades', { params: { categoria } });
  return data;
}

export async function updateBinding(nome: string, template_versao_id: string): Promise<void> {
  await api.put(`/admin/templates/finalidades/${nome}/binding`, { template_versao_id });
}

// =====================================================
// Preview e Teste
// =====================================================

export async function previewTemplate(body: {
  conteudo: string;
  categoria: string;
  finalidade_id?: string;
}): Promise<TemplatePreviewResponse> {
  const { data } = await api.post<TemplatePreviewResponse>('/admin/templates/preview', body);
  return data;
}

export async function sendTestEmail(body: {
  conteudo: string;
  categoria: string;
  finalidade_id?: string;
  to_email: string;
}): Promise<void> {
  await api.post('/admin/templates/test-email', body);
}

// =====================================================
// Email Queue
// =====================================================

export async function fetchEmailQueue(params: {
  status?: string;
  search?: string;
  page?: number;
  page_size?: number;
}): Promise<EmailQueueListResponse> {
  const { data } = await api.get<EmailQueueListResponse>('/admin/emails/', { params });
  return data;
}

export async function fetchEmailStats(): Promise<EmailQueueStats> {
  const { data } = await api.get<EmailQueueStats>('/admin/emails/stats');
  return data;
}

export async function fetchEmailDetail(emailId: string): Promise<EmailQueueDetail> {
  const { data } = await api.get<EmailQueueDetail>(`/admin/emails/${emailId}`);
  return data;
}

export interface ResendEmailResponse {
  ok: boolean;
  email_id: string;
  original_email_id?: string;
  mode: 'reset_in_place' | 'clone';
}

export async function resendEmail(emailId: string): Promise<ResendEmailResponse> {
  const { data } = await api.post<ResendEmailResponse>(`/admin/emails/${emailId}/resend`);
  return data;
}
